import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { isEqual } from 'lodash-es';

import { IServerOrder } from '@rbi-ctg/menu';
import { GetOrderDocument } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { apolloClient } from 'state/graphql/client';
import { OrderStatus } from 'state/order/types';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { useCheckoutState } from '../checkout-provider';

import { useCheckoutTotals } from './use-checkout-totals';

interface FetchOrderOptions {
  orderId: string;
  isSilentFetch: boolean;
}

/**
 * Resolves order ID from URL params or Redux.
 * URL params take precedence for deep linking support.
 */
const useResolveOrderId = (): string | null => {
  const params = useLocalSearchParams<{ rbiOrderId?: string }>();
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const router = useRouter();
  const [checkoutState] = useCheckoutState();

  const urlRbiOrderId = params.rbiOrderId;

  useFocusEffect(
    useCallback(() => {
      // If no order ID from either source, redirect to home (unless submitting)
      if (!urlRbiOrderId && !pricedOrderId && !checkoutState.isSubmittingOrder) {
        logger.warn({
          message: '[useResolveOrderId] No order ID found - redirecting to home',
        });
        router.replace(routes.cart);
      }
    }, [urlRbiOrderId, pricedOrderId, checkoutState.isSubmittingOrder, router])
  );

  // URL takes precedence, fall back to Redux
  return urlRbiOrderId || pricedOrderId;
};

/**
 * Fetches and validates order data from the server.
 * Handles both initial fetches (with loading state) and silent fetches (during repricing).
 */
const useFetchOrder = () => {
  const [checkoutState, setCheckoutState] = useCheckoutState();
  const router = useRouter();
  const lastFetchedOrderIdRef = useRef<string | null>(null);
  const isPricingOrder = useAppSelector(selectors.ordering.selectIsPricingOrder);

  const queryOrder = useCallback(async (rbiOrderId: string): Promise<IServerOrder | null> => {
    const { data, errors } = await apolloClient.query<{ order: IServerOrder }>({
      fetchPolicy: 'network-only',
      query: GetOrderDocument,
      variables: {
        id: rbiOrderId,
      },
    });

    if (errors) {
      logger.error({ errors, message: '[useCheckoutInit][useFetchOrder] Error querying order' });
      throw errors;
    }

    return data?.order ?? null;
  }, []);

  const shouldSkipFetch = useCallback(
    (orderId: string): boolean => {
      const { serverOrder, isSubmittingOrder, wasCommitOrderCalled } = checkoutState;
      const currentServerOrderId = serverOrder?.rbiOrderId ?? null;

      // Skip if order is being submitted
      if (isSubmittingOrder && wasCommitOrderCalled) {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Skipping - order is being submitted',
          orderId,
          isPricingOrder,
          wasCommitOrderCalled,
        });
        return true;
      }

      // Skip if we already have this exact order
      if (currentServerOrderId === orderId) {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Skipping - order already loaded',
          orderId,
        });
        return true;
      }

      // Skip if we're already fetching or have just fetched this order
      if (lastFetchedOrderIdRef.current === orderId) {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Skipping - already fetching this order',
          orderId,
        });
        return true;
      }

      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Skip check - proceeding',
        orderId,
        currentServerOrderId,
        lastFetchedOrderId: lastFetchedOrderIdRef.current,
        isPricingOrder,
        isSubmittingOrder,
        wasCommitOrderCalled,
      });
      return false;
    },
    [checkoutState, isPricingOrder]
  );

  const validateAndUpdateOrder = useCallback(
    (serverOrder: IServerOrder, orderId: string, isInitialFetch: boolean) => {
      setCheckoutState(prev => {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] validateAndUpdateOrder',
          orderId,
          isInitialFetch,
          serverOrderId: serverOrder.rbiOrderId,
          serverOrderServiceMode: serverOrder.cart?.serviceMode,
          prevServerOrderId: prev.serverOrder?.rbiOrderId,
          prevServiceMode: prev.serverOrder?.cart?.serviceMode,
          isSubmittingOrder: prev.isSubmittingOrder,
        });
        // Skip validation if order is being submitted
        if (prev.isSubmittingOrder) {
          logger.debug({
            message: '[useCheckoutInit][useFetchOrder] Skipping validation - order being submitted',
            orderId,
            status: serverOrder.status,
          });
          return {
            ...prev,
            serverOrder,
            isOrderLoading: isInitialFetch ? false : prev.isOrderLoading,
          };
        }

        // Validate order status
        if (serverOrder.status !== OrderStatus.PRICE_SUCCESSFUL) {
          logger.warn({
            message: '[useCheckoutInit][useFetchOrder] Invalid order status',
            orderId,
            status: serverOrder.status,
          });
          router.replace(routes.base);
          return {
            ...prev,
            isOrderLoading: isInitialFetch ? false : prev.isOrderLoading,
          };
        }

        logger.debug({
          message: isInitialFetch
            ? '[useCheckoutInit][useFetchOrder] Successfully loaded order'
            : '[useCheckoutInit][useFetchOrder] Successfully fetched repriced order silently',
          orderId,
        });

        return {
          ...prev,
          serverOrder,
          isOrderLoading: isInitialFetch ? false : prev.isOrderLoading,
        };
      });
    },
    [router, setCheckoutState]
  );

  const fetchOrder = useCallback(
    async ({ orderId, isSilentFetch }: FetchOrderOptions) => {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] fetchOrder invoked',
        orderId,
        isSilentFetch,
        currentServerOrderId: checkoutState.serverOrder?.rbiOrderId,
        currentServiceMode: checkoutState.serverOrder?.cart?.serviceMode,
        isSubmittingOrder: checkoutState.isSubmittingOrder,
        lastFetchedOrderId: lastFetchedOrderIdRef.current,
        isPricingOrder,
        wasCommitOrderCalled: checkoutState.wasCommitOrderCalled,
      });
      // Check if we should skip this fetch
      if (shouldSkipFetch(orderId)) {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Skip fetch',
          orderId,
          isSilentFetch,
          currentServerOrderId: checkoutState.serverOrder?.rbiOrderId,
          currentServiceMode: checkoutState.serverOrder?.cart?.serviceMode,
          isSubmittingOrder: checkoutState.isSubmittingOrder,
          lastFetchedOrderId: lastFetchedOrderIdRef.current,
        });
        return;
      }

      // Mark as being fetched to prevent duplicates
      lastFetchedOrderIdRef.current = orderId;

      const isInitialFetch = !isSilentFetch;

      if (isSilentFetch) {
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Fetching repriced order silently',
          currentOrderId: checkoutState.serverOrder?.rbiOrderId,
          newOrderId: orderId,
        });
      }

      try {
        // Set loading state only for initial fetch
        if (isInitialFetch) {
          setCheckoutState(prev => ({ ...prev, isOrderLoading: true }));
        }

        // Fetch the order from server
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Querying order',
          orderId,
          isSilentFetch,
        });
        const serverOrder = await queryOrder(orderId);
        logger.debug({
          message: '[useCheckoutInit][useFetchOrder] Query result',
          orderId,
          serverOrderId: serverOrder?.rbiOrderId ?? null,
          serverOrderServiceMode: serverOrder?.cart?.serviceMode ?? null,
        });

        if (!serverOrder) {
          logger.warn({
            message: '[useCheckoutInit][useFetchOrder] Failed to fetch order',
            orderId,
          });
          router.replace(routes.base);
          return;
        }

        // Validate and update state
        validateAndUpdateOrder(serverOrder, orderId, isInitialFetch);
      } catch (error) {
        logger.error({
          message: '[useCheckoutInit][useFetchOrder] Error fetching order',
          error,
          orderId,
        });

        // Clear loading state on error
        if (isInitialFetch) {
          setCheckoutState(prev => ({ ...prev, isOrderLoading: false }));
        }

        router.replace(routes.base);
      }
    },
    // Intentionally minimal dependencies to keep fetchOrder stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldSkipFetch, queryOrder, validateAndUpdateOrder]
  );

  return { fetchOrder };
};

/**
 * Synchronizes calculated totals with checkout state.
 * Updates state only when totals actually change to prevent unnecessary re-renders.
 */
const useSyncTotals = () => {
  const [, setCheckoutState] = useCheckoutState();
  const totals = useCheckoutTotals();

  useEffect(() => {
    setCheckoutState(prev => {
      // Skip update if totals haven't changed
      if (isEqual(prev.totals, totals)) {
        return prev;
      }

      return { ...prev, totals };
    });
  }, [totals, setCheckoutState]);
};

/**
 * Initializes and manages the checkout page state.
 *
 * This hook orchestrates three main concerns:
 * 1. Order ID resolution from URL or Redux
 * 2. Order data fetching on mount and after repricing
 * 3. Totals calculation synchronization
 */
export const useCheckoutInit = () => {
  const orderId = useResolveOrderId();
  const isPricingOrder = useAppSelector(selectors.ordering.selectIsPricingOrder);
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);

  // Track if we've already fetched on mount
  const hasFetchedOnMountRef = useRef(false);
  // Track previous pricing state to detect completion
  const prevIsPricingRef = useRef(isPricingOrder);
  // Track previous order ID to detect late updates (e.g., service mode changes)
  const prevOrderIdRef = useRef<string | null>(null);
  // Track previous priced order ID to catch delayed updates
  const prevPricedOrderIdRef = useRef<string | null>(null);

  // Sync calculated totals with checkout state
  useSyncTotals();

  const { fetchOrder } = useFetchOrder();

  useEffect(() => {
    // Wait for order ID to be available
    if (!orderId) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] No orderId yet',
        orderId,
        pricedOrderId,
        isPricingOrder,
      });
      return;
    }

    const prevOrderId = prevOrderIdRef.current;
    const orderIdChanged = Boolean(prevOrderId && prevOrderId !== orderId);
    const prevPricedOrderId = prevPricedOrderIdRef.current;
    const pricedOrderIdChanged = Boolean(
      pricedOrderId && prevPricedOrderId && pricedOrderId !== prevPricedOrderId
    );

    // Detect repricing completion (isPricingOrder went from true to false)
    const repricingJustCompleted = prevIsPricingRef.current && !isPricingOrder;
    prevIsPricingRef.current = isPricingOrder;

    logger.debug({
      message: '[useCheckoutInit][useFetchOrder] Effect tick',
      orderId,
      pricedOrderId,
      isPricingOrder,
      prevOrderId,
      prevPricedOrderId,
      orderIdChanged,
      pricedOrderIdChanged,
      repricingJustCompleted,
    });

    // Case 1: Initial mount - fetch the order
    if (!hasFetchedOnMountRef.current && !isPricingOrder) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Fetching order on mount',
        orderId,
      });
      hasFetchedOnMountRef.current = true;
      fetchOrder({
        orderId,
        isSilentFetch: false,
      });
      prevOrderIdRef.current = orderId;
      prevPricedOrderIdRef.current = pricedOrderId ?? orderId;
      return;
    }

    // Case 1b: Order ID changed outside repricing completion (e.g. late pricedOrderId update)
    if (orderIdChanged && !isPricingOrder) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Order ID changed, fetching new order',
        oldOrderId: prevOrderId,
        newOrderId: orderId,
      });
      fetchOrder({
        orderId,
        isSilentFetch: true,
      });
      prevOrderIdRef.current = orderId;
      prevPricedOrderIdRef.current = pricedOrderId ?? orderId;
      return;
    }

    // Case 2: Repricing completed - fetch the NEW order
    // Service mode change produces a new pricedOrderId
    if (repricingJustCompleted && pricedOrderId && pricedOrderId !== orderId) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Repricing completed, fetching new order',
        oldOrderId: orderId,
        newOrderId: pricedOrderId,
      });
      fetchOrder({
        orderId: pricedOrderId,
        isSilentFetch: true,
      });
      prevPricedOrderIdRef.current = pricedOrderId ?? orderId;
      return;
    }

    // Case 3: Repricing completed but same order ID - refetch to get updated data
    if (repricingJustCompleted && pricedOrderId === orderId) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Repricing completed, refetching order',
        orderId: pricedOrderId,
      });
      fetchOrder({
        orderId: pricedOrderId,
        isSilentFetch: true,
      });
      prevPricedOrderIdRef.current = pricedOrderId ?? orderId;
      return;
    }

    // Case 4: Priced order ID updated after pricing completed (late update)
    if (!isPricingOrder && pricedOrderIdChanged) {
      logger.debug({
        message: '[useCheckoutInit][useFetchOrder] Priced order ID changed, fetching new order',
        oldOrderId: prevPricedOrderId,
        newOrderId: pricedOrderId,
      });
      fetchOrder({
        orderId: pricedOrderId ?? '',
        isSilentFetch: true,
      });
      prevPricedOrderIdRef.current = pricedOrderId;
      return;
    }
    if (!prevOrderIdRef.current) {
      prevOrderIdRef.current = orderId;
    }
    if (!prevPricedOrderIdRef.current) {
      prevPricedOrderIdRef.current = pricedOrderId ?? orderId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, isPricingOrder, pricedOrderId]);
};
